import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { loginRequest } from "../../store/features/user/userActions";
import {
  FiArrowLeft,
  FiMail,
  FiLock,
  FiX,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

interface LoginProps {
  isModal?: boolean;
  onSwitch?: () => void;
  onClose?: () => void;
}

const Login: React.FC<LoginProps> = ({
  isModal = false,
  onSwitch,
  onClose,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { isAuthenticated, loading, subscription } = useSelector(
    (state: RootState) => state.user,
  );

  const from = (location.state as any)?.from || "/";

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError("Email is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }
    dispatch(loginRequest({ email, password }));
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (isModal && onClose) {
        onClose();
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from, isModal, onClose]);

  return (
    <div
      className={`relative flex w-full ${
        isModal ? "h-full min-h-[500px]" : "min-h-screen"
      } overflow-hidden`}
    >
      {/* ===== Full Background (Fintech themed, light, subtle) ===== */}
      {!isModal && (
        <div className="absolute inset-0 -z-10">
          {/* Base light gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50" />

          {/* Soft spotlight glows */}
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-indigo-200/35 blur-3xl" />
          <div className="absolute -bottom-40 left-1/4 h-[28rem] w-[28rem] rounded-full bg-cyan-200/25 blur-3xl" />

          {/* Fintech pattern overlay (SVG, very low opacity) */}
          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1400' height='900' viewBox='0 0 1400 900'%3E%3Cdefs%3E%3CradialGradient id='g' cx='50%25' cy='45%25' r='70%25'%3E%3Cstop offset='0%25' stop-color='%230ea5e9' stop-opacity='0.22'/%3E%3Cstop offset='55%25' stop-color='%236366f1' stop-opacity='0.10'/%3E%3Cstop offset='100%25' stop-color='%23ffffff' stop-opacity='0'/%3E%3C/radialGradient%3E%3C/defs%3E%3Crect width='1400' height='900' fill='url(%23g)'/%3E%3C!-- cards --%3E%3Cg fill='none' stroke='%231e293b' stroke-opacity='0.18'%3E%3Crect x='90' y='120' rx='18' ry='18' width='290' height='180'/%3E%3Crect x='1050' y='160' rx='18' ry='18' width='290' height='180'/%3E%3Crect x='870' y='560' rx='18' ry='18' width='360' height='220'/%3E%3C/g%3E%3C!-- graph lines --%3E%3Cg fill='none' stroke='%230ea5e9' stroke-opacity='0.30' stroke-width='3'%3E%3Cpath d='M130 710 C260 620, 320 740, 430 650 S650 610, 770 520 S980 540, 1120 420'/%3E%3C/g%3E%3Cg fill='none' stroke='%236366f1' stroke-opacity='0.22' stroke-width='3'%3E%3Cpath d='M160 760 C300 700, 360 790, 480 720 S690 680, 830 610 S1030 620, 1210 520'/%3E%3C/g%3E%3C!-- nodes (transactions) --%3E%3Cg fill='%230ea5e9' fill-opacity='0.22'%3E%3Ccircle cx='430' cy='650' r='6'/%3E%3Ccircle cx='770' cy='520' r='6'/%3E%3Ccircle cx='1120' cy='420' r='6'/%3E%3C/g%3E%3Cg fill='%236366f1' fill-opacity='0.18'%3E%3Ccircle cx='480' cy='720' r='6'/%3E%3Ccircle cx='830' cy='610' r='6'/%3E%3Ccircle cx='1210' cy='520' r='6'/%3E%3C/g%3E%3C!-- subtle dotted grid --%3E%3Cg fill='%230f172a' fill-opacity='0.06'%3E%3Ccircle cx='240' cy='260' r='2'/%3E%3Ccircle cx='280' cy='260' r='2'/%3E%3Ccircle cx='320' cy='260' r='2'/%3E%3Ccircle cx='240' cy='300' r='2'/%3E%3Ccircle cx='280' cy='300' r='2'/%3E%3Ccircle cx='320' cy='300' r='2'/%3E%3Ccircle cx='240' cy='340' r='2'/%3E%3Ccircle cx='280' cy='340' r='2'/%3E%3Ccircle cx='320' cy='340' r='2'/%3E%3C/g%3E%3C!-- small UPI-style arrows --%3E%3Cg stroke='%231e40af' stroke-opacity='0.20' stroke-width='3' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M980 310 h90'/%3E%3Cpath d='M1060 290 l20 20 -20 20'/%3E%3Cpath d='M1020 360 h-90'/%3E%3Cpath d='M940 340 l-20 20 20 20'/%3E%3C/g%3E%3C/svg%3E\")",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />

          {/* Readability wash */}
          <div className="absolute inset-0 bg-white/35 backdrop-blur-[1px]" />
        </div>
      )}

      {/* Main Form Container */}
      <div
        className={`w-full flex flex-col justify-center items-center p-8 bg-transparent relative z-10`}
      >
        {!isModal && (
          <RouterLink
            to="/"
            className="absolute top-8 left-8 flex items-center gap-2 text-gray-700 hover:text-blue-600 transition-colors font-medium bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full"
          >
            <FiArrowLeft /> Back to Home
          </RouterLink>
        )}

        {isModal && onClose && (
          <button
            onClick={onClose}
            className="absolute top-8 right-12 p-2 text-gray-400 hover:text-gray-600 transition-colors z-50"
          >
            <FiX size={24} />
          </button>
        )}

        {/* Glass Card Container */}
        <div className="w-full max-w-md bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20 relative">
          {/* {isModal && onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-gray-900 hover:text-gray-700 transition-colors z-50 rounded-full hover:bg-gray-100"
            >
              <FiX size={24} />
            </button>
          )} */}

          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600 text-lg">
              Sign in to manage your payments efficiently.
            </p>
          </div>
          <form onSubmit={handleCredentialsSubmit} className="space-y-6 mt-6">
            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-semibold text-gray-700"
              >
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                  <FiMail size={20} />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-semibold text-gray-700"
              >
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500 group-focus-within:text-blue-600 transition-colors">
                  <FiLock size={20} />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="block w-full pl-10 pr-10 py-3 border border-gray-200/60 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white hover:bg-white/80 focus:bg-white"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-blue-600 transition-colors cursor-pointer focus:outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>

            {(error || subscription.error) && (
              <div className="mt-6 bg-transparent text-red-700 p-4 rounded-md shadow-sm">
                {/* <p className="font-medium">Error</p> */}
                <p className="text-sm">{error || subscription.error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-lg shadow-blue-600/20 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all active:scale-[0.98] duration-200"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-600">
            {`Don't have an account?`}
            {isModal ? (
              <button
                onClick={onSwitch}
                className="font-semibold text-blue-600 hover:text-blue-500 transition-colors hover:underline"
              >
                Create an account
              </button>
            ) : (
              <RouterLink
                to="/register"
                className="font-semibold text-blue-600 hover:text-blue-500 transition-colors hover:underline"
              >
                Create an account
              </RouterLink>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
